<script module lang="ts">
	/**
	 * Abstract SPA Router — pathname-based SPA router for Svelte.
	 *
	 * Originally derived from svelte-spa-router v3.3.0 (MIT, Alessandro Segala).
	 * Substantially rewritten for AbstractID2: pathname routing, basePath support,
	 * TypeScript, and hash-mode removal.  See LICENSE.md for attribution.
	 */
	import { readable, writable, derived } from 'svelte/store';
	import type { Readable, Writable } from 'svelte/store';
	import { tick } from 'svelte';
	import type { Component } from 'svelte';

	// ── Base-path support ─────────────────────────────────────────
	// Call `setBasePath('/Manage')` once at app startup to make this
	// router transparently handle a path prefix.  `getLocation()`
	// strips the prefix before route matching, and `push()` /
	// `replace()` prepend it when writing to the History API.

	/** Module-level base path (empty string = no prefix). */
	let _basePath: string = '';

	/**
	 * Top-level paths the same app also owns, outside the base path.
	 *
	 * The admin app lives under `/Manage`, but the storefront is served by
	 * that very app at `/Store` — one SPA, so moving between the two is a
	 * route change rather than a page load. A path under one of these roots
	 * is neither prefixed by `push()` nor stripped by the location store,
	 * so its route keys are written whole (`'/Store/Product/:slug'`).
	 */
	let _roots: string[] = [];

	/**
	 * Configure a base path that is automatically prepended by
	 * `push()` / `replace()` and stripped by the location store.
	 *
	 * Call once during app bootstrap, **before** mounting the root
	 * component.
	 *
	 * @param bp - Path prefix without a trailing slash, e.g. `'/Manage'`.
	 * @param roots - Further top-level paths the app owns outside the prefix, e.g. `['/Store']`.
	 */
	export function setBasePath(bp: string, roots: string[] = []): void {
		_basePath = bp.endsWith('/') ? bp.slice(0, -1) : bp;
		_roots = roots.map((root) => (root.endsWith('/') ? root.slice(0, -1) : root));

		/* If the loc store is already initialised (its start function
       ran before this call), re-evaluate so subscribers see the
       correct basePath-stripped location immediately. */
		if (internal_setLocation) {
			internal_setLocation(getLocation());
		}
	}

	/**
	 * Whether a path sits under one of the extra roots, and so is written
	 * to the address bar exactly as the route names it.
	 *
	 * Matches whole segments only: `/Store` and `/Store/Account` are under
	 * the `/Store` root, `/Storeroom` is not.
	 */
	function isUnderRoot(path: string): boolean {
		return _roots.some((root) => path === root || path.startsWith(`${root}/`) || path.startsWith(`${root}?`));
	}

	/** The address-bar form of a route path: prefixed, unless it belongs to an extra root. */
	function toBrowserPath(path: string): string {
		return isUnderRoot(path) ? path : _basePath + path;
	}

	/** Full location: pathname and querystring. */
	export interface LocationInfo {
		/** Current pathname, e.g. `/book`. */
		location: string;
		/** Raw querystring (without the leading `?`). */
		querystring: string;
	}

	/** Route details passed to pre-condition functions and router events. */
	export interface RouteDetail {
		/** Route pattern that matched (string or RegExp). */
		route: string | RegExp;
		/** Current pathname. */
		location: string;
		/** Current querystring. */
		querystring: string;
		/** Params matched in the route */
		params: Record<string, string> | null;
		/** Custom data passed by the user */
		userData?: object;
	}

	/** Detail object for the `routeLoaded` event */
	export interface RouteDetailLoaded extends RouteDetail {
		/** Svelte component */
		component: Component;
		/** Name of the Svelte component that was loaded (note: might be minified in production) */
		name: string;
	}

	/** A Svelte component loaded asynchronously via dynamic `import()`. */
	export type AsyncSvelteComponent = () => Promise<{ default: Component }>;

	/**
	 * Route pre-condition callback. Returns `true` to allow the route or `false`
	 * to block it.  May be asynchronous.
	 */
	export type RoutePrecondition = (_detail: RouteDetail) => boolean | Promise<boolean>;

	/** Object returned by the `wrap` helper. */
	export interface WrappedComponent {
		/** Async component loader. */
		component: AsyncSvelteComponent & { loading?: Component; loadingParams?: object };
		/** Route pre-conditions to validate before loading. */
		conditions?: RoutePrecondition[];
		/** Static props passed to the component. */
		props?: Record<string, unknown>;
		/** Custom user data forwarded to route events. */
		userData?: object;
		/** Internal flag — identifies objects created by `wrap()`. */
		_abstractRouter: true;
	}

	/** Type for the opts parameter of the link action */
	export type LinkActionOpts = {
		/** A string to use in place of the link's href attribute. Using this allows for updating link's targets reactively. */
		href?: string;
		/** If true, link is disabled */
		disabled?: boolean;
	};

	/** Scroll state stored in history entries. */
	interface ScrollState {
		__abstract_router_scrollX: number;
		__abstract_router_scrollY: number;
	}

	/** Route definition: path → component mapping (plain object or Map). */
	export type RouteDefinition = Record<string, any> | Map<string | RegExp, any>;

	/** Generic interface for events from the router */
	export interface RouterEvent<T> {
		detail: T;
	}

	/** Event type for conditionsFailed */
	export type ConditionsFailedEvent = RouterEvent<RouteDetail>;
	/** Event type for routeLoading */
	export type RouteLoadingEvent = RouterEvent<RouteDetail>;
	/** Event type for routeLoaded */
	export type RouteLoadedEvent = RouterEvent<RouteDetailLoaded>;

	/**
	 * Returns the current location from the pathname, stripping
	 * the configured `_basePath` prefix (if any) so that route
	 * definitions never need to reference the prefix.
	 */
	function getLocation(): LocationInfo {
		let location = window.location.pathname;

		/* Strip basePath prefix so route definitions stay clean. A path under
		   an extra root is already its own route key and is left whole. */
		if (_basePath && !isUnderRoot(location) && location.startsWith(_basePath)) {
			location = location.substring(_basePath.length) || '/';
		}

		// Check if there's a querystring
		const qsPosition = window.location.href.indexOf('?');
		let querystring = '';
		// Extract the querystring portion from the URL if present
		if (qsPosition > -1) {
			querystring = window.location.href.substr(qsPosition + 1);
		}

		return { location, querystring };
	}

	/**
	 * Internal setter for the location store, assigned by the readable store's start function.
	 */
	let internal_setLocation: ((_value: LocationInfo) => void) | undefined = undefined;

	/**
	 * Readable store that returns the current full location (incl. querystring)
	 */
	export const loc: Readable<LocationInfo> = readable<LocationInfo>(
		null as unknown as LocationInfo,
		function start(set: (_value: LocationInfo) => void) {
			internal_setLocation = set;
			set(getLocation());
			/** Re-evaluates the current location when a popstate event fires. */
			const fnUpdate = (): void => {
				set(getLocation());
			};
			window.addEventListener('popstate', fnUpdate, false);

			return function stop(): void {
				window.removeEventListener('popstate', fnUpdate, false);
			};
		}
	);

	/**
	 * Readable store that returns the current location
	 */
	export const location: Readable<string> = derived(loc, ($loc: LocationInfo) => $loc.location);

	/**
	 * Readable store that returns the current querystring
	 */
	export const querystring: Readable<string> = derived(loc, ($loc: LocationInfo) => $loc.querystring);

	/**
	 * Store that returns the currently-matched params.
	 * Despite this being writable, consumers should not change the value of the store.
	 * It is exported as a readable store only (in the typings)
	 */
	export const params: Writable<Record<string, string> | undefined> = writable(undefined);

	/**
	 * Navigates to a new page programmatically.
	 *
	 * @param location - Path to navigate to (must start with `/` or '#/')
	 * @returns Promise that resolves after the page navigation has completed
	 */
	export async function push(location: string): Promise<void> {
		if (!location || location.length < 1 || location.charAt(0) != '/') {
			throw Error('Invalid parameter location');
		}

		// Execute this code when the current call stack is complete
		await tick();

		/* Prepend _basePath so the browser URL includes the prefix
       while route definitions stay prefix-free. */
		const dest = toBrowserPath(location);

		// Save current scroll position, then push with scroll reset
		history.replaceState(
			{ ...history.state, __abstract_router_scrollX: window.scrollX, __abstract_router_scrollY: window.scrollY },
			undefined as unknown as string
		);
		history.pushState(
			{ ...history.state, __abstract_router_scrollX: 0, __abstract_router_scrollY: 0 },
			undefined as unknown as string,
			dest
		);
		internal_setLocation!(getLocation());

		// this is a forward navigation: scroll to top
		window.scrollTo(0, 0);
	}

	/**
	 * Navigates back in history (equivalent to pressing the browser's back button).
	 *
	 * @returns Promise that resolves after the page navigation has completed
	 */
	export async function pop(): Promise<void> {
		// Execute this code when the current call stack is complete
		await tick();

		window.history.back();
	}

	/**
	 * Replaces the current page but without modifying the history stack.
	 *
	 * @param location - Path to navigate to (must start with `/` or '#/')
	 * @returns Promise that resolves after the page navigation has completed
	 */
	export async function replace(location: string): Promise<void> {
		if (!location || location.length < 1 || location.charAt(0) != '/') {
			throw Error('Invalid parameter location');
		}

		/* Prepend _basePath so the browser URL includes the prefix. */
		location = toBrowserPath(location);

		// Execute this code when the current call stack is complete
		await tick();

		// Attempt to replace history state, catching errors in restricted environments (e.g. Svelte REPL)
		try {
			const newState: Record<string, unknown> = {
				...history.state
			};
			delete newState['__abstract_router_scrollX'];
			delete newState['__abstract_router_scrollY'];
			window.history.replaceState(newState, undefined as unknown as string, location);
		} catch {
			console.warn(
				"Caught exception while replacing the current page. If you're running this in the Svelte REPL, please note that the `replace` method might not work in this environment."
			);
		}

		// replaceState doesn't trigger popstate, so update the location store manually
		internal_setLocation!(getLocation());
	}

	/**
	 * Handles an event by preventing the default and pushing the given href.
	 */
	export function onEventPushRoute(event: Event, href: string): void {
		event.preventDefault();
		push(href);
	}

	/**
	 * Svelte Action that enables a link element (`<a>`) to use our history management.
	 *
	 * For example:
	 *
	 * ```html
	 * <a href="/books" use:link>View books</a>
	 * ```
	 *
	 * @param node - The target node (automatically set by Svelte). Must be an anchor tag (`<a>`) with a href attribute starting in `/`
	 * @param opts - Options object or string href. For legacy reasons, we support a string too which will be the value for opts.href
	 */
	export function link(
		node: HTMLElement,
		opts?: string | LinkActionOpts
	): { update: (_updated?: string | LinkActionOpts) => void } {
		opts = linkOpts(opts);

		// Only apply to <a> tags
		if (!node || !node.tagName || node.tagName.toLowerCase() != 'a') {
			throw Error('Action "link" can only be used with <a> tags');
		}

		updateLink(node, opts);

		return {
			update(updated?: string | LinkActionOpts): void {
				updated = linkOpts(updated);
				updateLink(node, updated);
			}
		};
	}

	/**
	 * Restores scroll position from the given history state, or scrolls to top.
	 */
	export function restoreScroll(state?: ScrollState): void {
		if (state) {
			window.scrollTo(state.__abstract_router_scrollX, state.__abstract_router_scrollY);
		} else {
			// Otherwise this is a forward navigation: scroll to top
			window.scrollTo(0, 0);
		}
	}

	/** Internal function used by the link function */
	function updateLink(node: HTMLElement, opts: LinkActionOpts): void {
		let href = opts.href || node.getAttribute('href');

		// Destination must start with '/'
		if (!href || href.length < 1 || href[0] != '/') {
			throw Error('Invalid value for "href" attribute: ' + href);
		}

		node.setAttribute('href', href);
		node.addEventListener('click', (event: MouseEvent) => {
			// Prevent default anchor onclick behaviour
			event.preventDefault();
			if (!opts.disabled) {
				const target = event.currentTarget as HTMLElement;
				push(target.getAttribute('href')!);
			}
		});
	}

	/** Internal function that ensures the argument of the link action is always an object */
	function linkOpts(val?: string | LinkActionOpts): LinkActionOpts {
		if (val && typeof val == 'string') {
			return {
				href: val
			};
		} else {
			return (val as LinkActionOpts) || {};
		}
	}

	/**
	 * The handler attached to an anchor tag responsible for updating the
	 * current history state with the current scroll state
	 */
	function _scrollstateHistoryHandler(href: string): void {
		history.replaceState(
			{ ...history.state, __abstract_router_scrollX: window.scrollX, __abstract_router_scrollY: window.scrollY },
			undefined as unknown as string
		);
		// Navigate using pushState (pathname-based routing)
		history.pushState(history.state, undefined as unknown as string, toBrowserPath(href));
		internal_setLocation!(getLocation());
	}
</script>

<script lang="ts">
	import { onDestroy } from 'svelte';
	import { parse } from 'regexparam';

	interface Props {
		/**
		 * Dictionary of all routes, in the format `'/path': component`.
		 *
		 * For example:
		 * ```js
		 * import HomeRoute from './routes/HomeRoute.svelte'
		 * import BooksRoute from './routes/BooksRoute.svelte'
		 * import NotFoundRoute from './routes/NotFoundRoute.svelte'
		 * routes = {
		 *     '/': HomeRoute,
		 *     '/books': BooksRoute,
		 *     '*': NotFoundRoute
		 * }
		 * ```
		 */
		routes?: RouteDefinition;
		/**
		 * Optional prefix for the routes in this router. This is useful for example in the case of nested routers.
		 */
		prefix?: string | RegExp;
		/**
		 * If set to true, the router will restore scroll positions on back navigation
		 * and scroll to top on forward navigation.
		 */
		restoreScrollState?: boolean;
		/** Callback fired when route conditions fail. */
		onconditionsfailed?: (_detail: RouteDetail) => void;
		/** Callback fired when a route starts loading. */
		onrouteloading?: (_detail: RouteDetail) => void;
		/** Callback fired when a route finishes loading. */
		onrouteloaded?: (_detail: RouteDetailLoaded) => void;
	}

	let {
		routes = {},
		prefix = '',
		restoreScrollState = false,
		onconditionsfailed,
		onrouteloading,
		onrouteloaded
	}: Props = $props();

	/**
	 * Container for a route: path, component
	 */
	class RouteItem {
		/** The original path definition */
		path: string | RegExp;
		/** Component loader (always async) */
		component: WrappedComponent['component'];
		/** Pre-condition callbacks */
		conditions: RoutePrecondition[];
		/** Custom user data */
		userData?: object;
		/** Static props to pass to the component */
		props: Record<string, unknown>;
		/** Compiled regex pattern */
		private _pattern: RegExp;
		/** Parameter keys extracted from the path */
		private _keys: string[] | false;

		/**
		 * Initializes the object and creates a regular expression from the path, using regexparam.
		 *
		 * @param path - Path to the route (must start with '/' or '*')
		 * @param component - Svelte component for the route, optionally wrapped
		 */
		constructor(path: string | RegExp, component: Component | WrappedComponent) {
			if (
				!component ||
				(typeof component != 'function' &&
					(typeof component != 'object' || (component as WrappedComponent)._abstractRouter !== true))
			) {
				throw Error('Invalid component object');
			}

			// Path must be a regular expression, or a string starting with '/' or '*'
			if (
				!path ||
				(typeof path == 'string' && (path.length < 1 || (path.charAt(0) != '/' && path.charAt(0) != '*'))) ||
				(typeof path == 'object' && !(path instanceof RegExp))
			) {
				throw Error('Invalid value for "path" argument - strings must start with / or *');
			}

			const { pattern, keys } = parse(path as string);

			this.path = path;

			// Check if the component is wrapped and we have conditions
			if (typeof component == 'object' && (component as WrappedComponent)._abstractRouter === true) {
				const wrapped = component as WrappedComponent;
				this.component = wrapped.component;
				this.conditions = wrapped.conditions || [];
				this.userData = wrapped.userData;
				this.props = wrapped.props || {};
			} else {
				// Convert the component to a function that returns a Promise, to normalize it
				this.component = (() => Promise.resolve(component)) as unknown as WrappedComponent['component'];
				this.conditions = [];
				this.props = {};
			}

			this._pattern = pattern;
			this._keys = keys;
		}

		/**
		 * Checks if `path` matches the current route.
		 * If there's a match, will return the list of parameters from the URL (if any).
		 * In case of no match, the method will return `null`.
		 *
		 * @param path - Path to test
		 * @returns List of parameters from the URL if there's a match, or `null` otherwise.
		 */
		match(path: string): Record<string, string> | RegExpExecArray | null {
			// If there's a prefix, check if it matches the start of the path.
			// If not, bail early, else remove it before we run the matching.
			if (prefix) {
				if (typeof prefix == 'string') {
					if (path.startsWith(prefix)) {
						path = path.substr(prefix.length) || '/';
					} else {
						return null;
					}
				} else if (prefix instanceof RegExp) {
					const match = path.match(prefix);
					if (match && match[0]) {
						path = path.substr(match[0].length) || '/';
					} else {
						return null;
					}
				}
			}

			const matches = this._pattern.exec(path);
			// Return null when the route pattern does not match the given path
			if (matches === null) {
				return null;
			}

			// Return raw regex matches when the route was defined as a RegExp (no named keys)
			if (this._keys === false) {
				return matches;
			}

			const out: Record<string, string> = {};
			let i = 0;
			// Iterate over named keys and decode each matched parameter value
			while (i < this._keys.length) {
				// In the match parameters, URL-decode all values
				try {
					out[this._keys[i]] = decodeURIComponent(matches[i + 1] || '') || (null as unknown as string);
				} catch {
					out[this._keys[i]] = null as unknown as string;
				}
				i++;
			}
			return out;
		}

		/**
		 * Executes all conditions (if any) to control whether the route can be shown. Conditions are executed in the order they are defined, and if a condition fails, the following ones aren't executed.
		 *
		 * @param detail - Route detail
		 * @returns Returns true if all the conditions succeeded
		 */
		async checkConditions(detail: RouteDetail): Promise<boolean> {
			for (let i = 0; i < this.conditions.length; i++) {
				if (!(await this.conditions[i](detail))) {
					return false;
				}
			}

			return true;
		}
	}

	// Set up all routes
	const routesList: RouteItem[] = [];
	if (routes instanceof Map) {
		// If it's a map, iterate on it right away
		routes.forEach((route, path) => {
			routesList.push(new RouteItem(path, route));
		});
	} else {
		// We have an object, so iterate on its own properties
		Object.keys(routes).forEach((path) => {
			routesList.push(
				new RouteItem(
					path,
					(routes as Record<string, Component | WrappedComponent>)[path]
				)
			);
		});
	}

	// Props for the component to render
	let component: Component | null = $state(null);
	let componentParams: Record<string, string> | null = $state(null);
	let routeProps: Record<string, unknown> = $state({});

	/**
	 * Fires a router callback on the next tick to avoid synchronous side-effects.
	 *
	 * @param name - Event name to fire
	 * @param detail - Event detail payload
	 */
	async function dispatchNextTick(name: 'conditionsFailed' | 'routeLoading' | 'routeLoaded', detail: RouteDetail | RouteDetailLoaded): Promise<void> {
		// Execute this code when the current call stack is complete
		await tick();
		if (name === 'conditionsFailed') onconditionsfailed?.(detail);
		else if (name === 'routeLoading') onrouteloading?.(detail);
		else if (name === 'routeLoaded') onrouteloaded?.(detail as RouteDetailLoaded);
	}

	// If this is set, then that means we have popped into this var the state of our last scroll position
	let previousScrollState:
		| (Record<string, unknown> & { __abstract_router_scrollX?: number; __abstract_router_scrollY?: number })
		| null = null;

	// Update history.scrollRestoration depending on restoreScrollState
	$effect(() => {
		history.scrollRestoration = restoreScrollState ? 'manual' : 'auto';
	});

	let popStateChanged: ((_event: PopStateEvent) => void) | null = null;
	if (restoreScrollState) {
		popStateChanged = (event: PopStateEvent): void => {
			// If this event was from our history.replaceState, event.state will contain
			// our scroll history. Otherwise, event.state will be null (like on forward
			// navigation)
			if (event.state && (event.state.__abstract_router_scrollY || event.state.__abstract_router_scrollX)) {
				previousScrollState = event.state;
			} else {
				previousScrollState = null;
			}
			internal_setLocation!(getLocation());
		};
		// This is removed in the destroy() invocation below
		window.addEventListener('popstate', popStateChanged);

		$effect(() => {
			restoreScroll(
				previousScrollState as
					| { __abstract_router_scrollX: number; __abstract_router_scrollY: number }
					| undefined
			);
		});
	}

	// Always have the latest value of loc
	let lastLoc: LocationInfo | null = null;

	// Current object of the component loaded
	let componentObj: WrappedComponent['component'] | null = null;

	// Handle hash change events
	// Listen to changes in the $loc store and update the page
	// Do not use the $: syntax because it gets triggered by too many things
	const unsubscribeLoc = loc.subscribe(async (newLoc: LocationInfo) => {
		lastLoc = newLoc;

		// Find a route matching the location
		let i = 0;
		// Walk through registered routes until one matches the current location
		while (i < routesList.length) {
			const match = routesList[i].match(newLoc.location);
			if (!match) {
				i++;
				continue;
			}

			const detail: RouteDetail = {
				route: routesList[i].path,
				location: newLoc.location,
				querystring: newLoc.querystring,
				userData: routesList[i].userData,
				params:
					match && typeof match == 'object' && Object.keys(match).length
						? (match as Record<string, string>)
						: null
			};

			// Check if the route can be loaded - if all conditions succeed
			if (!(await routesList[i].checkConditions(detail))) {
				// Don't display anything
				component = null;
				componentObj = null;
				// Trigger a callback to notify the user, then exit
				dispatchNextTick('conditionsFailed', detail);
				return;
			}

			// Trigger a callback to alert that we're loading the route
			// We need to clone the object on every event invocation so we don't risk the object to be modified in the next tick
			dispatchNextTick('routeLoading', Object.assign({}, detail));

			// If there's a component to show while we're loading the route, display it
			const obj = routesList[i].component;
			// Do not replace the component if we're loading the same one as before, to avoid the route being unmounted and re-mounted
			if (componentObj != obj) {
				if (obj.loading) {
					component = obj.loading;
					componentObj = obj;
					componentParams = (obj.loadingParams as Record<string, string>) ?? null;
					routeProps = {};

					// Trigger the routeLoaded callback for the loading component
					// Create a copy of detail so we don't modify the object for the dynamic route (and the dynamic route doesn't modify our object too)
					dispatchNextTick(
						'routeLoaded',
						Object.assign({}, detail, {
							component: component,
							name: (component as { name?: string }).name,
							params: componentParams
						})
					);
				} else {
					component = null;
					componentObj = null;
				}

				// Invoke the Promise
				const loaded = await obj();

				// Now that we're here, after the promise resolved, check if we still want this component, as the user might have navigated to another page in the meanwhile
				if (newLoc != lastLoc) {
					// Don't update the component, just exit
					return;
				}

				// If there is a "default" property, which is used by async routes, then pick that
				component =
					(loaded as { default?: Component })?.default ||
					(loaded as unknown as Component);
				componentObj = obj;
			}

			// Set componentParams only if we have a match, to avoid a warning similar to `<Component> was created with unknown prop 'params'`
			// Of course, this assumes that developers always add a "params" prop when they are expecting parameters
			if (match && typeof match == 'object' && Object.keys(match).length) {
				componentParams = match as Record<string, string>;
			} else {
				componentParams = null;
			}

			// Set static props, if any
			routeProps = routesList[i].props;

			// Dispatch the routeLoaded callback then exit
			// We need to clone the object on every event invocation so we don't risk the object to be modified in the next tick
			dispatchNextTick(
				'routeLoaded',
				Object.assign({}, detail, {
					component: component,
					name: (component as { name?: string })?.name,
					params: componentParams
				})
			).then(() => {
				params.set(componentParams as Record<string, string> | undefined);
			});
			return;
		}

		// If we're still here, there was no match, so show the empty component
		component = null;
		componentObj = null;
		params.set(undefined);
	});

	onDestroy(() => {
		unsubscribeLoc();
		popStateChanged && window.removeEventListener('popstate', popStateChanged);
	});
</script>

{#if component}
	{#if componentParams}
		<svelte:component this={component} params={componentParams} {...routeProps} />
	{:else}
		<svelte:component this={component} {...routeProps} />
	{/if}
{/if}
